/**
 * Structural types for the dashboard's loosely-typed payloads.
 *
 * Everything here describes data that comes off the hub (widget rows, stored
 * widget config) or is assembled by the dashboard view for its children (the
 * `dashboardData` bundle). The hub model spells the config of a widget as
 * `widgetConfig: JsonValue` (see `@hub-types` `Widget`), and the views hand the
 * entity bundle down as a plain object, so the dashboard components used to
 * spell both as `any`. These are the narrow, structural stand-ins: the fields
 * the dashboard actually reads are declared, anything else stays a JSON value
 * and has to be narrowed at the call site.
 *
 * Card t_8bf0c819 (no-explicit-any batch 2/5), reconciled with the hub's
 * stage-6.1 sweep (`widgetConfig`/`layoutConfig`: `any` -> `JsonValue`) by card
 * t_9d7cfe72: a stand-in may only *narrow* a column it stands in for, so its
 * open bag is `JsonValue`-valued, never `unknown`.
 */
import type { DashboardWidget } from '@/types';
import type { Area, Device, Integration, Room, Scene } from '@/stores/entities';
import { isWireObject } from '@/utils/wire';
import type { DeviceDisplayMode } from '@/utils/deviceDisplay';
import type { JsonValue } from '@hub/types/json';
import type { InjectionKey } from 'vue';

/**
 * The layout a widget tile is hosted in, provided by `DashboardWidget` for the
 * cards that size themselves: `grid`/`floorplan`/`bim`/`scada` give a tile a
 * definite height, while `column` lays cards into a content-sized flow.
 */
export type WidgetLayoutType = 'grid' | 'column' | 'board' | 'floorplan' | 'scada' | 'bim';

export const WIDGET_LAYOUT_TYPE: InjectionKey<WidgetLayoutType> = Symbol('widgetLayoutType');

/**
 * A widget's stored config: layout position + size first (the two layouts that
 * read the config most heavily are the ones that read these), plus whatever keys
 * the widget's own card defines — those live behind the index signature and are
 * narrowed where they are read.
 *
 * The index signature is `JsonValue` because this type stands in for the hub's
 * column (`Widget.widgetConfig: JsonValue`), and a stand-in widens nothing: a
 * value it does not declare is still a value the column can hold, so a reader
 * narrows one step — from `JsonValue | undefined` to a string, a number or a row.
 */
export type WidgetConfigLike = {
  title?: string;
  /** Floor plan / BIM position and size, in canvas units. */
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  /** Size as the inline size editor writes it (same pair as `w`/`h`). */
  width?: number;
  height?: number;
  /** BIM 3D position. */
  x3d?: number;
  y3d?: number;
  z3d?: number;
  /** Grid / column layout slot. */
  col?: number;
  row?: number;
  colSpan?: number;
  rowSpan?: number;
  /** Stacking order of the control layouts. */
  order?: number;
  zIndex?: number;
  /**
   * How this placement draws in the 3D view — its pin (`icon`) or its 3D model
   * (`model`). `symbol` is a canvas mode and is never offered here; see
   * `utils/deviceDisplay.ts`, which also decides what the device can offer.
   */
  display?: DeviceDisplayMode;
  [key: string]: JsonValue | undefined;
};

/**
 * A widget row as the dashboard layouts and widget cards read it: the hub
 * `Widget` model, which is what the dashboard view hands down and what the
 * widget host takes as its `widget` prop, with the stored config narrowed from
 * the column's `JsonValue` to the bag the cards actually read.
 */
export interface DashboardWidgetLike extends DashboardWidget {
  widgetConfig: WidgetConfigLike;
}

/**
 * The stored config of a widget row, as the dashboard reads it: the row's
 * `widgetConfig` when it holds a JSON object, an empty bag otherwise (a widget
 * that was never configured stores none, and every reader defaults that way).
 */
export function widgetConfigOf(value: JsonValue | null | undefined): WidgetConfigLike {
  return isWireObject(value) ? (value as WidgetConfigLike) : {};
}

/**
 * A device row as the socket/store hands it over: the shared `Device` model plus
 * the keys the serializer keeps beside it — the row id repeated as `deviceId`
 * (what the dashboard's device pickers key on) and `category` (what the
 * device-list cards filter by).
 */
export interface DeviceRow extends Device {
  deviceId?: string;
  category?: string;
}

/** A scene row as the store hands it over: the shared model plus the serializer's `category`. */
export interface SceneRow extends Scene {
  category?: string;
}

/** One datapoint row inside `dashboardData.datapoints` (a hub datapoint record). */
export interface DashboardDatapointLike {
  datapoint_id: string;
  value?: unknown;
  timestamp?: string | number;
  [key: string]: unknown;
}

/**
 * The entity bundle the dashboard view computes and every layout / widget card
 * takes as its `dashboardData` prop. Every field is optional because
 * `DashboardWidget` falls back to the store's own (datapoints-only)
 * `DashboardData` when no bundle was passed down — readers must default.
 */
export interface DashboardDataLike {
  devices?: DeviceRow[];
  rooms?: Room[];
  areas?: Area[];
  scenes?: SceneRow[];
  integrations?: Integration[];
  datapoints?: Map<string, DashboardDatapointLike[]>;
  [key: string]: unknown;
}

/**
 * A dashboard's own layout config (`Dashboard.layoutConfig`): the knobs the
 * layout editors write beside the widget rows. Open-ended for the same reason the
 * widget config is — the hub stores it as free-form JSON — and `JsonValue`-valued
 * for the same reason too (see `WidgetConfigLike`).
 */
export type DashboardLayoutConfigLike = {
  columns?: number;
  rows?: number;
  displayMode?: string;
  belowColumns?: number;
  backgroundColor?: string;
  sizeFromImage?: boolean;
  aspectRatioPresetId?: string;
  customWidth?: number;
  customHeight?: number;
  customRatioX?: number;
  customRatioY?: number;
  /** Legacy size spelling; the migration path still reads and rewrites it. */
  layoutSizePresetId?: string;
  customLayoutWidth?: number;
  customLayoutHeight?: number;
  /** Ids of the widgets pinned into the layout's control strip. */
  layoutControls?: string[];
  /** Legacy control strip (superseded by `layoutControls`). */
  secondaryControls?: JsonValue;
  [key: string]: JsonValue | undefined;
};

/**
 * The `layoutConfig` of a dashboard row, as the layout surfaces read it: the
 * column when it holds a JSON object, an empty bag otherwise (a dashboard that
 * predates a knob simply has no key for it).
 */
export function layoutConfigOf(dashboard: { layoutConfig?: JsonValue } | null | undefined): DashboardLayoutConfigLike {
  const config = dashboard?.layoutConfig;
  return isWireObject(config) ? (config as DashboardLayoutConfigLike) : {};
}

/**
 * A floor-plan row as the floor-plan surfaces read it. The hub's `FloorPlan`
 * model carries `imagePath`; the API and the store also hand the widget-facing
 * surfaces an `imageUrl` alias plus the row's own `floorPlanId`, so both are
 * declared here.
 */
export interface FloorPlanLike {
  id?: string;
  floorPlanId?: string;
  imagePath?: string;
  imageUrl?: string;
  name?: string;
  width?: number;
  height?: number;
  [key: string]: unknown;
}

/**
 * Read a field off a `Record<string, unknown>`-shaped payload (stored configs,
 * API rows) without an `any` or a cast: the value comes back `unknown` and the
 * caller narrows it.
 */
export function readField(source: unknown, key: string): unknown {
  if (typeof source !== 'object' || source === null) return undefined;
  return (source as Record<string, unknown>)[key];
}
